import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Button from 'components/button/Button';
import useFormatCurrency from 'hooks/useFormatCurrency';
import { Property } from 'models/Properties';
import { HOSTFULLY_ASSIST_CURRENCY } from './HostfullyAssistDetails.constants';
import { HostfullyAssistDetailsFormSchemaType } from './HostfullyAssistDetails.schema';
import {
  PricingTable,
  PropertiesValue,
  SubCaption,
  TableScroller,
} from './HostfullyAssistDetails.styles';
import {
  getAssistSelectedUnitsCount,
  getAssistTotalPrice,
} from './HostfullyAssistDetails.utils';
import useAssistPropertiesSelectModal from './propertiesSelect/useAssistPropertiesSelectModal';

const CoverageTable = ({
  isEditDisabled,
  properties,
}: {
  isEditDisabled: boolean;
  properties: Property[];
}) => {
  const { t } = useTranslation();
  const formatCurrency = useFormatCurrency();

  const { control, setValue } =
    useFormContext<HostfullyAssistDetailsFormSchemaType>();

  const { openAssistPropertiesSelectModal } = useAssistPropertiesSelectModal();
  const selectedProperties = useWatch({ control, name: 'selectedProperties' });

  const handleOpenModal = () => {
    openAssistPropertiesSelectModal({
      selectedProperties,
      onUpdate: (properties: string[]) => {
        setValue('selectedProperties', properties);
      },
    });
  };

  return (
    <TableScroller>
      <PricingTable data-testid="assist-details-coverage">
        <caption>
          {t('pageHostfullyAssist.details.coverage.caption')}
          <SubCaption>
            {t('pageHostfullyAssist.details.coverage.subCaption')}
          </SubCaption>
        </caption>
        <tbody>
          <tr>
            <td>{t('pageHostfullyAssist.details.coverage.properties')}</td>
            <td>
              <PropertiesValue>
                <span data-testid="assist-details-properties-count">
                  {t(
                    'pageHostfullyAssist.details.coverage.propertiesSelected',
                    {
                      count: getAssistSelectedUnitsCount(
                        selectedProperties,
                        properties,
                      ),
                    },
                  )}
                </span>
                <Button
                  bsStyle="link"
                  disabled={isEditDisabled}
                  onClick={handleOpenModal}
                  data-testid="assist-details-edit-properties"
                >
                  {t('common.edit')}
                </Button>
              </PropertiesValue>
            </td>
          </tr>
          <tr>
            <td>{t('common.total')}</td>
            <td data-testid="assist-details-total">
              {t('pageHostfullyAssist.details.coverage.totalValue', {
                total: formatCurrency({
                  value: getAssistTotalPrice(selectedProperties, properties),
                  currency: HOSTFULLY_ASSIST_CURRENCY,
                }),
              })}
            </td>
          </tr>
        </tbody>
      </PricingTable>
    </TableScroller>
  );
};

export default CoverageTable;
